import { Stack, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Button } from 'settle-ui/components/Button/Button'
import { CheckboxController } from 'settle-ui/form/CheckboxController/CheckboxController'
import { TextFieldController } from 'settle-ui/form/TextFieldController/TextFieldController'
import { Link } from 'settle-ui/components/Link/Link'
import { BusinessIndustrySelect } from 'routes/components/BusinessIndustrySelect/BusinessIndustrySelect'
import { EMAIL_PATTERN, WEBSITE_PATTERN } from 'lib/patterns'
import { marketingLinks } from 'lib/consts/externalLinks'
import { logEvent } from 'lib/tracking/segment'
import { type PayersGetStartedPageFormValues } from '../../PayersGetStartedPage'
import { usePayersGetStartedFormSubmit } from '../../hooks/usePayersGetStartedFormSubmit/usePayersGetStartedFormSubmit'
import * as events from '../../PayersGetStartedPage.events'

interface PayersGetStartedFormProps {
  invitationCode: string | null
  partner_source: string | null
  utm_source: string | null
  utm_campaign: string | null
  source_page: string | null
}

export const PayersGetStartedForm: Settle.FC<PayersGetStartedFormProps> = ({
  invitationCode,
  partner_source,
  utm_source,
  utm_campaign,
  source_page,
}) => {
  const { t } = useTranslation()
  const form = useFormContext<PayersGetStartedPageFormValues>()
  const { handleSubmit, loading } = usePayersGetStartedFormSubmit({
    invitationCode,
    partner_source,
    utm_source,
    utm_campaign,
    source_page,
  })

  return (
    <Stack
      component="form"
      noValidate={true}
      onSubmit={handleSubmit}
      sx={{
        gap: 2,
        width: '100%',
        maxWidth: 380,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 1.5,
          justifyContent: 'stretch',
        }}
      >
        <TextFieldController
          margin="none"
          name="firstName"
          control={form.control}
          label={t('firstName')}
          rules={{ required: { value: true, message: t('validation::cantBeEmpty') } }}
          onFocus={() => logEvent(events.FOCUS_FIRST_NAME)}
        />
        <TextFieldController
          margin="none"
          name="lastName"
          control={form.control}
          label={t('lastName')}
          rules={{ required: { value: true, message: t('validation::cantBeEmpty') } }}
          onFocus={() => logEvent(events.FOCUS_LAST_NAME)}
        />
      </Stack>
      <TextFieldController
        margin="none"
        name="email"
        control={form.control}
        label={t('workEmail')}
        rules={{
          required: { value: true, message: t('validation::cantBeEmpty') },
          pattern: { value: EMAIL_PATTERN, message: t('emailIsIncorrect') },
        }}
        onFocus={() => logEvent(events.FOCUS_EMAIL)}
      />
      <TextFieldController
        margin="none"
        name="businessName"
        control={form.control}
        label={t('businessName')}
        rules={{ required: { value: true, message: t('validation::cantBeEmpty') } }}
        onFocus={() => logEvent(events.FOCUS_BUSINESS_NAME)}
      />
      <TextFieldController
        margin="none"
        name="website"
        control={form.control}
        label={t('businessWebsite')}
        required={true}
        rules={{
          pattern: { value: WEBSITE_PATTERN, message: t('validation::websitePattern') },
        }}
        onFocus={() => logEvent(events.FOCUS_WEBSITE)}
      />
      <BusinessIndustrySelect
        name="businessIndustry"
        label={t('whatDoesBusinessDo')}
        required={true}
        margin="none"
        rules={{ required: { value: true, message: t('validation::cantBeEmpty') } }}
        onOpen={() => logEvent(events.FOCUS_BUSINESS_INDUSTRY)}
      />
      <CheckboxController
        control={form.control}
        sx={{ ml: 0, mt: -1.5 }}
        rules={{ required: true }}
        name="prohibitedIndustryConfirmation"
        disableTypography={true}
        label={
          <Typography variant="caption">
            <Trans
              i18nKey="onboarding::bizDetails::unsupportedIndustry"
              components={{
                industriesLink: <Link variant="inherit" href={marketingLinks.unsupportedIndustries} target="_blank" />,
              }}
            />
          </Typography>
        }
      />
      <Button type="submit" color="primary" fullWidth={true} variant="primary" loading={loading}>
        {t('next')}
      </Button>
    </Stack>
  )
}
